import Axios from '@/utils/axios';
import { getBackendErrorMessage } from '@/utils/errorHandler';

/**
 * Driver list for the dispatch flow — `GET /v3/drivers` (same endpoint as
 * `services/api/drivers.ts`, kept separate here because the dispatch flow
 * only needs the snapshot fields and adds the `tagNames` recommendation
 * filter).
 */
export interface DispatchDriver {
  id: number;
  code: string;
  first_name: string;
  last_name: string;
  phone_number?: string;
  is_active?: boolean;
  is_approved?: boolean;
  is_blacklist?: boolean;
  status?: string;
  city?: { id: number; name: string };
  /**
   * Providers (= clients) the driver is registered under in driver-service.
   * Seeds the default client scope when the driver is added to a zone roster.
   */
  driver_providers?: {
    id: number;
    name: string;
    code?: string;
    revenue_stream?: string;
  }[];
}

export interface DispatchDriverListResponse {
  data: DispatchDriver[];
  pagination?: {
    total: number;
    page: number;
    size: number;
    lastPage?: number;
    last_page?: number;
  };
}

export interface DispatchDriverListParams {
  page?: number;
  size?: number;
  search?: string;
  sort?: string;
  order?: string;
  active?: boolean;
  status?: string;
  /**
   * Filter drivers by tag name — the dispatch flow tags riders with the
   * hub name they usually serve, so `tagNames=<hub name>` returns the
   * "recommended" riders for that hub.
   */
  tagNames?: string;
}

const DEFAULTS: Required<
  Pick<DispatchDriverListParams, 'page' | 'size' | 'sort' | 'order'>
> = {
  page: 1,
  size: 100,
  sort: 'created_at',
  order: 'desc',
};

export const dispatchDriversAPI = {
  list: async (
    params: DispatchDriverListParams = {}
  ): Promise<DispatchDriverListResponse> => {
    const query: Record<string, string> = {
      page: String(params.page ?? DEFAULTS.page),
      size: String(params.size ?? DEFAULTS.size),
      sort: params.sort ?? DEFAULTS.sort,
      order: params.order ?? DEFAULTS.order,
    };
    if (params.search) query.search = params.search;
    if (params.active !== undefined) query.active = String(params.active);
    if (params.status) query.status = params.status;
    if (params.tagNames) query.tagNames = params.tagNames;
    try {
      const response = await Axios.get('/v3/drivers', { params: query });
      return response.data as DispatchDriverListResponse;
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },
};

export function driverDisplayName(d: DispatchDriver): string {
  return `${d.first_name ?? ''} ${d.last_name ?? ''}`.trim() || d.code;
}
